import z, { ZodType } from "zod";

import { looseUuid } from "@thalamos/common";

export const OrganisationStatusValues = ["active", "deleted"] as const;
export const OrganisationStatusSchema = z.enum(OrganisationStatusValues);
export type OrganisationStatus = (typeof OrganisationStatusValues)[number];

export const OrganisationRoleValues = [
  "police/force",
  "police/geographic-unit",
  "police/national-unit",
  "police/command-unit",
  "police/regional-unit",
  "admin",
] as const;
export type OrganisationRole = (typeof OrganisationRoleValues)[number];
export const OrganisationRoleSchema = z.enum({
  PoliceForce: "police/force",
  PoliceGeographicUnit: "police/geographic-unit",
  PoliceNationalUnit: "police/national-unit",
  PoliceCommandUnit: "police/command-unit",
  PoliceRegionalUnit: "police/regional-unit",
  Admin: "admin",
} satisfies Record<string, OrganisationRole>);

export type OrganisationIdUnbranded = string;
export const OrganisationIdSchema =
  looseUuid().brand<"vision-common:OrganisationId">() satisfies ZodType<OrganisationIdUnbranded>;
export type OrganisationId = z.infer<typeof OrganisationIdSchema>;

export type OrganisationUnbranded = {
  id: OrganisationIdUnbranded;
  name: string;
  address: string;
  postalCode: string;
  managingOrganisationId: OrganisationIdUnbranded | null;
  role: OrganisationRole;
  status: OrganisationStatus;
  /**
   * When true, this organisation will not appear in organisation selectors used
   * to record where a detention took place (e.g. "In which BCU did the
   * detention take place?"). It will still appear in the onboarding home-base
   * selector so that non-BCU staff can complete onboarding.
   *
   * Defaults to false. The database column is NOT NULL DEFAULT FALSE.
   */
  excludeAsIncidentLocation: boolean;
};

export const OrganisationSchema = z
  .object({
    id: OrganisationIdSchema,
    name: z.string().min(1).max(255),
    address: z.string().max(255),
    postalCode: z.string().max(20),
    managingOrganisationId: OrganisationIdSchema.nullable(),
    role: OrganisationRoleSchema,
    status: OrganisationStatusSchema,
    // Use .default(false) so that: the TypeScript type is `boolean` (non-optional,
    // matching the NOT NULL DB column), and callers that do not supply the field
    // (e.g. create-org requests) receive a safe default automatically.
    excludeAsIncidentLocation: z.boolean().default(false),
  })
  .brand<"vision-common:Organisation">() satisfies ZodType<OrganisationUnbranded>;

export type Organisation = z.infer<typeof OrganisationSchema>;

export type ExtendedOrganisationUnbranded = OrganisationUnbranded & {
  managingOrganisationName: string | null;
};

export const ExtendedOrganisationSchema = OrganisationSchema.extend({
  managingOrganisationName: z.string().max(255).nullable(),
}).brand<"vision-common:ExtendedOrganisation">() satisfies ZodType<ExtendedOrganisationUnbranded>;

export type ExtendedOrganisation = z.infer<typeof ExtendedOrganisationSchema>;

export const OrganisationArraySchema = z.array(OrganisationSchema);
export type OrganisationArray = z.infer<typeof OrganisationArraySchema>;

export const ExtendedOrganisationArraySchema = z.array(
  ExtendedOrganisationSchema,
);
export type ExtendedOrganisationArray = z.infer<
  typeof ExtendedOrganisationArraySchema
>;

export const OrganisationSearchResponseSchema = z.object({
  count: z.number(),
  results: OrganisationArraySchema,
});

export type AvailableOrganisationUnbranded = {
  id: OrganisationIdUnbranded;
  name: string;
  managingOrganisationId: OrganisationIdUnbranded | null;
  role: OrganisationRole;
  /**
   * When true, this organisation will not appear in organisation selectors used
   * to record where a detention took place. See {@link OrganisationUnbranded}.
   *
   * Defaults to false. The database column is NOT NULL DEFAULT FALSE.
   */
  excludeAsIncidentLocation: boolean;
};

export const AvailableOrganisationSchema = z
  .object({
    id: OrganisationIdSchema,
    name: z.string().min(1).max(255),
    managingOrganisationId: OrganisationIdSchema.nullable(),
    role: OrganisationRoleSchema,
    excludeAsIncidentLocation: z.boolean().default(false),
  })
  .brand<"vision-common:AvailableOrganisation">() satisfies ZodType<AvailableOrganisationUnbranded>;

export type AvailableOrganisation = z.infer<typeof AvailableOrganisationSchema>;

export const AvailableOrganisationArraySchema = z.array(
  AvailableOrganisationSchema,
);
export type AvailableOrganisationArray = z.infer<
  typeof AvailableOrganisationArraySchema
>;

export const UserOrganisationRoleValue = [
  "officer",
  "supervisor",
  "client-admin",
  "analytics-viewer",
  "guest-user",
] as const;
export type UserOrganisationRole = (typeof UserOrganisationRoleValue)[number];
export const UserOrganisationRoleSchema = z.enum({
  Officer: "officer",
  Supervisor: "supervisor",
  ClientAdmin: "client-admin",
  AnalyticsViewer: "analytics-viewer",
  GuestUser: "guest-user",
} satisfies Record<string, UserOrganisationRole>);

export type UserOrganisationMembershipUnbranded = {
  organisationId: OrganisationIdUnbranded;
  role: UserOrganisationRole;
  organisationName: string;
  organisationRole: OrganisationRole;
  managingOrganisationId: OrganisationIdUnbranded | null;
  isOnboarded?: boolean;
  type: string;
};

export const UserOrganisationMembershipSchema = z
  .object({
    organisationId: OrganisationIdSchema,
    role: UserOrganisationRoleSchema,
    organisationName: z.string(),
    organisationRole: OrganisationRoleSchema,
    managingOrganisationId: OrganisationIdSchema.nullable(),
    isOnboarded: z.boolean().optional(),
    type: z.string(),
  })
  .brand<"vision-common:UserOrganisationMembership">() satisfies ZodType<UserOrganisationMembershipUnbranded>;

export type UserOrganisationMembership = z.infer<
  typeof UserOrganisationMembershipSchema
>;

export type CreateOrganisationRequest = Omit<
  OrganisationUnbranded,
  "id" | "status" | "excludeAsIncidentLocation"
> & {
  excludeAsIncidentLocation?: boolean;
};

export const CreateOrganisationSchema = z.object({
  name: z.string().min(1).max(255),
  address: z.string().min(1).max(255),
  postalCode: z.string().min(1).max(20),
  managingOrganisationId: OrganisationIdSchema.nullable(),
  role: OrganisationRoleSchema,
  excludeAsIncidentLocation: z.boolean().optional(),
});

export const UpdateOrganisationSchema = z.object({
  name: z.string().min(1).max(255),
  address: z.string().max(255),
  postalCode: z.string().max(20),
  managingOrganisationId: OrganisationIdSchema.nullable(),
  role: OrganisationRoleSchema,
  status: OrganisationStatusSchema,
  excludeAsIncidentLocation: z.boolean().optional(),
});

export type UpdateOrganisationRequest = Partial<CreateOrganisationRequest> & {
  status?: OrganisationStatus;
};
